import React from "react";
import { Alert, Box, Button, Card, Column, Columns, Stack, Text, TextField } from "braid-design-system";
import { useAuth } from "../contexts/AuthProvider";
import ForbiddenAccessPage from "./ForbiddenAccessPage";
import {
    getUserSignOnRecords,
    updateUserSignOnRecords,
    UserSignOnRecord,
    getServerDateTime,
    ServerDateTime,
    getStations,
    StationOption,
} from "../services/signOnService";
import { getStationId } from "../utils/terminalStation";

const normalizeRole = (value: string | null | undefined): string =>
    (value || "").trim().toUpperCase();

const canAccessSignOn = (role: string | null | undefined): boolean => {
    const normalized = normalizeRole(role);
    return normalized === "CASHIER" || normalized === "ADMIN";
};

const SignOn: React.FC = () => {
    // A cashier can only sign on to the counter this terminal is set up as.
    const selectedStationId = getStationId() || "";
    const { user } = useAuth();
    const hasSignOnAccess = canAccessSignOn(user?.user_role as string | null | undefined);

    const [stations, setStations] = React.useState<StationOption[]>([]);
    const [serverDateTime, setServerDateTime] = React.useState<ServerDateTime | null>(null);
    const [clientNow, setClientNow] = React.useState(() => new Date());
    const userName = user?.user_name || "-";
    const [signOnRecord, setSignOnRecord] = React.useState<UserSignOnRecord | null>(null);
    const [isLoading, setIsLoading] = React.useState(false);
    const [errorMessage, setErrorMessage] = React.useState<string | null>(null);
    const [openingBalance, setOpeningBalance] = React.useState("");
    const [openingBalanceError, setOpeningBalanceError] = React.useState<string | null>(null);

    React.useEffect(() => {
        const timer = setInterval(() => setClientNow(new Date()), 1000);
        return () => clearInterval(timer);
    }, []);

    const loadStationsList = React.useCallback(async () => {
        try {
            const result = await getStations();
            setStations(result.stations);
        } catch {
            // Silently fall back to existing stations
        }
    }, []);

    // Fetch server datetime and stations on mount
    React.useEffect(() => {
        if (!hasSignOnAccess) {
            return;
        }

        const fetchInitialData = async () => {
            try {
                const dt = await getServerDateTime();
                setServerDateTime(dt);
            } catch {
                // Silently fail - fallback to client
            }
            await loadStationsList();
            if (user?.user_name) {
                try {
                    const record = await getUserSignOnRecords(user.user_name);
                    if (record) {
                        setSignOnRecord(record);
                    }
                } catch {
                    // Ignore record lookup errors on initial load
                }
            }
        };

        void fetchInitialData();
    }, [hasSignOnAccess, loadStationsList, user?.user_name]);

    const clientDate = React.useMemo(() => {
        const yyyy = String(clientNow.getFullYear());
        const mm = String(clientNow.getMonth() + 1).padStart(2, "0");
        const dd = String(clientNow.getDate()).padStart(2, "0");
        return `${yyyy}-${mm}-${dd}`;
    }, [clientNow]);

    const clientTime = React.useMemo(() => {
        const hh = String(clientNow.getHours()).padStart(2, "0");
        const min = String(clientNow.getMinutes()).padStart(2, "0");
        const ss = String(clientNow.getSeconds()).padStart(2, "0");
        return `${hh}:${min}:${ss}`;
    }, [clientNow]);

    const selectedStation = stations.find((s) => s.station_id === selectedStationId) ?? null;
    const isStationOccupiedByOther = Boolean(
        selectedStation?.is_occupied && selectedStation.occupied_by && selectedStation.occupied_by !== user?.user_name
    );

    const handleOpeningBalanceChange = (value: string) => {
        if (/^\d*$/.test(value)) {
            setOpeningBalance(value);
            setOpeningBalanceError(null);
            return;
        }
        setOpeningBalanceError("Opening Balance must contain numbers only");
    };

    const handleSignOn = async () => {
        setIsLoading(true);
        setErrorMessage(null);
        try {
            if (!hasSignOnAccess) {
                setErrorMessage("Only users with CASHIER or ADMIN role can sign on.");
                return;
            }

            if (!user?.user_name) {
                setErrorMessage("User not found");
                return;
            }

            if (!selectedStationId) {
                setErrorMessage("This terminal is not set up for a counter. Please complete terminal setup first.");
                return;
            }

            if (isStationOccupiedByOther && selectedStation) {
                setErrorMessage(
                    `Counter ${selectedStation.station_id} is already in use by cashier '${selectedStation.occupied_by}'. No other users are allowed to sign on to this counter.`
                );
                return;
            }

            let latestServerDateTime: ServerDateTime | null = serverDateTime;
            try {
                const dt = await getServerDateTime();
                setServerDateTime(dt);
                latestServerDateTime = dt;
            } catch {
                // ignore
            }

            const userNameToCheck = user.user_name;
            const record = await getUserSignOnRecords(userNameToCheck);

            if (record && record.signon === 1) {
                const activeStation = record.stationid != null ? String(record.stationid) : "-";
                const signOnDate = record.signondate ?? "-";
                setErrorMessage(
                    `User '${userNameToCheck}' is already signed on to Counter ${activeStation} on ${signOnDate}. Sign on remains active until you sign off.`
                );
                return;
            }

            // Validate opening balance is required
            if (openingBalance === "" || openingBalance === "0") {
                setOpeningBalanceError("Opening Balance is required");
                return;
            }

            const openingBalanceNumber = parseFloat(openingBalance);
            const floatAmtValue = isNaN(openingBalanceNumber) ? 0 : openingBalanceNumber;

            const signOnDateToSend = (latestServerDateTime?.date || clientDate || "").trim();
            const signOnDateTimeToSend = (latestServerDateTime?.datetime || `${clientDate} ${clientTime}` || "").trim();

            const updatedRecord = await updateUserSignOnRecords({
                user_name: userNameToCheck,
                signon: 1,
                signondate: signOnDateToSend || undefined,
                signontime: signOnDateTimeToSend || undefined,
                station_id: selectedStationId,
                floatamt: floatAmtValue,
            });

            if (updatedRecord) {
                setSignOnRecord(updatedRecord);
                window.dispatchEvent(new CustomEvent("webpos:signon-status-changed"));
                await loadStationsList();
            } else {
                setErrorMessage(`Unable to update sign-on record for user: ${userNameToCheck}`);
            }
        } catch (error: any) {
            setErrorMessage(error.message || "Failed to sign on cashier");
        } finally {
            setIsLoading(false);
        }
    };

    if (!hasSignOnAccess) {
        return <ForbiddenAccessPage message="Only users with CASHIER or ADMIN role can sign on." />;
    }

    const currentShift = signOnRecord?.signon === 1
        ? (signOnRecord.shiftno || 1)
        : (selectedStation?.next_shift ?? 1);

    return (
        <Box padding="medium">
            <Card>
                <Box padding="medium">
                    <Stack space="medium">
                        <Text weight="strong" size="large">Cashier Sign On</Text>

                        <Columns space="medium" collapseBelow="tablet">
                            <Column>
                                <Stack space="xsmall">
                                    <Text tone="secondary">User Name</Text>
                                    <Box background="surface" borderRadius="standard" padding="small">
                                        <Text>{userName}</Text>
                                    </Box>
                                </Stack>
                            </Column>

                            <Column>
                                <Stack space="xsmall">
                                    <Text tone="secondary">Counter</Text>
                                    <Box background="surface" borderRadius="standard" padding="small">
                                        <Text>{selectedStationId ? `Counter ${selectedStationId}` : "Not set up"}</Text>
                                    </Box>
                                </Stack>
                            </Column>

                            <Column>
                                <Stack space="xsmall">
                                    <Text tone="secondary">{signOnRecord?.signon === 1 ? "Active Shift" : "Next Shift"}</Text>
                                    <Box background="surface" borderRadius="standard" padding="small">
                                        <Text weight="strong">{`Shift ${currentShift}`}</Text>
                                    </Box>
                                </Stack>
                            </Column>

                            <Column>
                                <Stack space="xsmall">
                                    <Text tone="secondary">Sign On Date</Text>
                                    <Box background="surface" borderRadius="standard" padding="small">
                                        <Text>{serverDateTime?.date || clientDate}</Text>
                                    </Box>
                                </Stack>
                            </Column>

                            <Column>
                                <Stack space="xsmall">
                                    <Text tone="secondary">Sign On Time</Text>
                                    <Box background="surface" borderRadius="standard" padding="small">
                                        <Text>{serverDateTime?.time || clientTime}</Text>
                                    </Box>
                                </Stack>
                            </Column>
                        </Columns>

                        {isStationOccupiedByOther && selectedStation ? (
                            <Alert tone="critical">
                                <Text>
                                    {`Counter ${selectedStation.station_id} is already in use by cashier '${selectedStation.occupied_by}'. No other users are allowed to sign on to this counter.`}
                                </Text>
                            </Alert>
                        ) : null}

                        {signOnRecord?.signon === 1 ? (
                            <Alert tone="info">
                                <Text>
                                    {`You are currently signed on to Counter ${signOnRecord.stationid} (Shift ${signOnRecord.shiftno || 1}). Sign on remains active until you explicitly sign off.`}
                                </Text>
                            </Alert>
                        ) : (
                            <>
                                <Box>
                                    <div style={{ width: "220px" }}>
                                        <TextField
                                            label="Opening Balance"
                                            value={openingBalance}
                                            onChange={(e) => handleOpeningBalanceChange(e.currentTarget.value)}
                                            tone={openingBalanceError ? "critical" : undefined}
                                            message={openingBalanceError || undefined}
                                        />
                                    </div>
                                </Box>

                                <Box>
                                    <div style={{ width: "140px" }}>
                                        <Button
                                            variant="solid"
                                            tone={isStationOccupiedByOther || !selectedStationId ? "neutral" : "brandAccent"}
                                            onClick={isStationOccupiedByOther || !selectedStationId || isLoading ? undefined : handleSignOn}
                                            loading={isLoading}
                                        >
                                            Sign On
                                        </Button>
                                    </div>
                                </Box>
                            </>
                        )}

                        {errorMessage ? (
                            <Alert tone="critical">
                                <Text>{errorMessage}</Text>
                            </Alert>
                        ) : null}

                        {signOnRecord !== null && signOnRecord.signon === 1 ? (
                            <Alert tone="positive">
                                <Text>
                                    {`Sign on completed for Counter ${signOnRecord.stationid != null ? String(signOnRecord.stationid) : selectedStationId} - Shift ${signOnRecord.shiftno || 1} on ${signOnRecord.signondate ?? clientDate}.`}
                                </Text>
                            </Alert>
                        ) : null}
                    </Stack>
                </Box>
            </Card>
        </Box>
    );
};

export default SignOn;
